import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, X, Send, Check, Clock, Flag, Users, BarChart2,
  BookOpen, Target, Navigation, ZapOff, Anchor, Zap,
  Calendar, Bot, TrendingUp, AlertTriangle, CheckCircle2,
  ArrowUpRight, RefreshCw, Sparkles, Palette, ChevronDown,
} from "lucide-react";
import { useTheme, THEMES } from "../contexts/ThemeContext";

const GROQ_KEY = "gsk_a1x8Y1We4lXApLGdyb3FYJzskxWqXtX7UJn8rReUVmKcA";
const ROW_H = 160;   // px per row unit
const COLS = 4;
const GAP  = 14;

// ─── Widget definitions ────────────────────────────────────────────────────────
const WIDGET_DEFS = {
  ai:          { label: "AI Assistant",   icon: Bot,        accent: "#7C6CF8", defaultW: 2, defaultH: 3 },
  daily:       { label: "Today",          icon: Calendar,   accent: "#00C781", defaultW: 2, defaultH: 2 },
  capture:     { label: "Quick Capture",  icon: Zap,        accent: "#F5C842", defaultW: 1, defaultH: 1 },
  commitments: { label: "Commitments",    icon: Check,      accent: "#00C781", defaultW: 1, defaultH: 2 },
  boards:      { label: "Active Boards",  icon: Flag,       accent: "#A855F7", defaultW: 2, defaultH: 2 },
  compass:     { label: "Compass",        icon: Target,     accent: "#5A90F5", defaultW: 1, defaultH: 2 },
  drift:       { label: "Drift",          icon: Navigation, accent: "#14B8A6", defaultW: 1, defaultH: 2 },
  tension:     { label: "Tension",        icon: ZapOff,     accent: "#EF4444", defaultW: 1, defaultH: 2 },
  timedebt:    { label: "Time Debt",      icon: Clock,      accent: "#F59E0B", defaultW: 1, defaultH: 2 },
  buoy:        { label: "Buoy",           icon: Anchor,     accent: "#60B8E0", defaultW: 1, defaultH: 2 },
};

const DEFAULT_LAYOUT = [
  { id:"w1", type:"ai",          w:2, h:3 },
  { id:"w2", type:"daily",       w:2, h:2 },
  { id:"w3", type:"capture",     w:1, h:1 },
  { id:"w4", type:"boards",      w:2, h:2 },
  { id:"w5", type:"commitments", w:1, h:2 },
  { id:"w6", type:"tension",     w:1, h:2 },
  { id:"w7", type:"timedebt",    w:1, h:2 },
];

function loadLayout() { try { return JSON.parse(localStorage.getItem("cresc:dash-v3")||"null"); } catch { return null; } }
function saveLayout(l) { try { localStorage.setItem("cresc:dash-v3", JSON.stringify(l)); } catch {} }

function greet() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

// ─── Widget content components ────────────────────────────────────────────────

function AIWidget({ wsId, accent }) {
  const [msgs, setMsgs] = useState([{ role:"assistant", content:"Ask me about your projects, commitments, or what to focus on today." }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [ctx, setCtx] = useState(null);
  const bottomRef = useRef(null);
  useEffect(() => {
    if (!wsId) return;
    Promise.all([
      api.get(`/workspaces/${wsId}/boards`).catch(()=>({data:[]})),
      api.get(`/workspaces/${wsId}/commitments`).catch(()=>({data:[]})),
    ]).then(([b,c]) => setCtx({ boards: b.data?.slice(0,4).map(x=>x.name)||[], commitments: c.data?.filter(x=>x.status==="active").slice(0,4).map(x=>x.title)||[] }));
    setMsgs([{ role:"assistant", content:"Ask me about your projects, commitments, or what to focus on today." }]);
  }, [wsId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [msgs]);
  const send = async () => {
    const text = input.trim(); if (!text||loading) return;
    setInput(""); setLoading(true);
    const next = [...msgs, { role:"user", content:text }];
    setMsgs(next);
    const sys = `You are Crescendo assistant. Boards: ${ctx?.boards?.join(", ")||"none"}. Commitments: ${ctx?.commitments?.join(", ")||"none"}. Be concise, max 80 words.`;
    try {
      const res = await fetch("https://api.groq.com/openai/v1/chat/completions", { method:"POST",
        headers:{"Content-Type":"application/json", Authorization:`Bearer ${GROQ_KEY}`},
        body:JSON.stringify({ model:"llama-3.3-70b-versatile", messages:[{role:"system",content:sys},...next.slice(-6)], max_tokens:200 }) });
      const j = await res.json();
      setMsgs(m => [...m, { role:"assistant", content:j.choices?.[0]?.message?.content||"Try again." }]);
    } catch { setMsgs(m => [...m, { role:"assistant", content:"Network error." }]); }
    finally { setLoading(false); }
  };
  return (
    <div className="flex flex-col h-full gap-2">
      <div className="flex-1 overflow-y-auto space-y-2 min-h-0" style={{scrollbarWidth:"none"}}>
        {msgs.map((m,i) => (
          <div key={i} className={`text-[11.5px] leading-relaxed rounded-xl px-3 py-2.5 ${m.role==="user"?"ml-6":""}`}
            style={{ background: m.role==="user" ? `${accent}18` : "var(--cresc-surface-2)", color: "var(--cresc-text-2)" }}>
            {m.content}
          </div>
        ))}
        {loading && <div className="flex gap-1 px-3 py-2 rounded-xl w-fit" style={{background:"var(--cresc-surface-2)"}}>
          {[0,1,2].map(i=><div key={i} className="w-1.5 h-1.5 rounded-full animate-bounce" style={{background:"rgba(255,255,255,0.3)",animationDelay:`${i*.12}s`}}/>)}
        </div>}
        <div ref={bottomRef}/>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()}
          placeholder="Ask something…" className="flex-1 rounded-xl px-3 py-2 text-[11.5px] outline-none"
          style={{background:"var(--cresc-surface-2)",border:"1px solid var(--cresc-border)",color:"var(--cresc-text)"}}/>
        <button onClick={send} disabled={!input.trim()||loading}
          className="w-8 h-8 rounded-xl flex items-center justify-center disabled:opacity-30"
          style={{background:accent}}>
          <Send className="w-3.5 h-3.5 text-black"/>
        </button>
      </div>
    </div>
  );
}

function TodayWidget({ wsId, accent }) {
  const [events, setEvents] = useState([]);
  const today = new Date().toISOString().slice(0,10);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/events`).then(({data})=>setEvents((data||[]).filter(e=>e.start?.slice(0,10)===today&&!e.unscheduled).sort((a,b)=>a.start.localeCompare(b.start)))).catch(()=>{});
  }, [wsId, today]);
  const now = new Date();
  return (
    <div className="flex flex-col h-full gap-3">
      <div className="flex items-baseline gap-2">
        <span className="text-[24px] font-bold text-white">{now.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</span>
        <span className="text-[11px]" style={{color:"var(--cresc-text-3)"}}>{now.toLocaleDateString([],{weekday:"long",month:"short",day:"numeric"})}</span>
      </div>
      {events.length===0 ? (
        <p className="text-[11px]" style={{color:"var(--cresc-text-4)"}}>Nothing scheduled today</p>
      ) : (
        <div className="flex-1 space-y-2 overflow-y-auto min-h-0" style={{scrollbarWidth:"none"}}>
          {events.map(ev=>(
            <div key={ev.event_id} className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl" style={{background:"var(--cresc-surface-2)"}}>
              <div className="w-0.5 self-stretch rounded-full" style={{background: ev.custom_color||accent}}/>
              <div>
                <p className="text-[11.5px] font-medium text-white">{ev.title}</p>
                <p className="text-[9.5px]" style={{color:"var(--cresc-text-3)"}}>{new Date(ev.start).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CaptureWidget({ wsId, accent }) {
  const [text, setText] = useState("");
  const [recent, setRecent] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(()=>{ if(wsId) api.get(`/workspaces/${wsId}/captures`).then(({data})=>setRecent((data||[]).slice(0,3))).catch(()=>{}); },[wsId]);
  const save = async () => {
    if(!text.trim()||saving) return;
    setSaving(true);
    try { const {data} = await api.post(`/workspaces/${wsId}/captures`,{title:text.trim()}); setText(""); setRecent(r=>[data,...r].slice(0,3)); toast.success("Captured"); }
    catch {} finally { setSaving(false); }
  };
  return (
    <div className="flex flex-col h-full gap-2">
      <div className="flex gap-1.5">
        <input value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==="Enter"&&save()}
          placeholder="Capture a thought…" className="flex-1 rounded-xl px-3 py-2 text-[11.5px] outline-none"
          style={{background:"var(--cresc-surface-2)",border:"1px solid var(--cresc-border)",color:"var(--cresc-text)"}}/>
        <button onClick={save} disabled={!text.trim()||saving}
          className="w-8 h-8 rounded-xl flex items-center justify-center disabled:opacity-30"
          style={{background:accent}}>
          <Plus className="w-3.5 h-3.5 text-black"/>
        </button>
      </div>
      {recent.map(c=><p key={c.capture_id} className="text-[10px] truncate" style={{color:"var(--cresc-text-3)"}}>· {c.title}</p>)}
    </div>
  );
}

function BoardsWidget({ wsId, accent }) {
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  useEffect(()=>{ if(wsId) api.get(`/workspaces/${wsId}/boards`).then(({data})=>setBoards((data||[]).slice(0,4))).catch(()=>{}); },[wsId]);
  return (
    <div className="flex flex-col h-full gap-2 overflow-y-auto" style={{scrollbarWidth:"none"}}>
      {boards.length===0 ? (
        <div className="flex-1 flex items-center justify-center">
          <button onClick={()=>navigate("/boards")} className="text-[11px]" style={{color:accent}}>Create a board →</button>
        </div>
      ) : boards.map(b=>{
        const pct = b.step_count>0 ? Math.round(b.completed_count/b.step_count*100) : 0;
        const c = b.color||accent;
        return (
          <div key={b.board_id} onClick={()=>navigate("/boards")}
            className="p-3 rounded-xl cursor-pointer transition-all"
            style={{background:"var(--cresc-surface-2)",border:"1px solid var(--cresc-border)"}}
            onMouseEnter={e=>e.currentTarget.style.background="var(--cresc-surface-3)"}
            onMouseLeave={e=>e.currentTarget.style.background="var(--cresc-surface-2)"}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11.5px] font-medium text-white truncate">{b.emoji} {b.name}</p>
              <span className="text-[10px] font-mono" style={{color:c}}>{pct}%</span>
            </div>
            <div className="h-1 rounded-full overflow-hidden" style={{background:"var(--cresc-surface-3)"}}>
              <div className="h-full rounded-full" style={{width:`${pct}%`,background:c}}/>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CommitmentsWidget({ wsId, accent }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(()=>{ if(wsId) api.get(`/workspaces/${wsId}/commitments`,{params:{status:"active"}}).then(({data})=>setItems((data||[]).sort((a,b)=>(a.due_date||"z").localeCompare(b.due_date||"z")).slice(0,5))).catch(()=>{}); },[wsId]);
  const now = new Date().toISOString();
  return (
    <div className="flex flex-col h-full gap-1.5 overflow-y-auto" style={{scrollbarWidth:"none"}}>
      {items.length===0 ? <div className="flex-1 flex items-center justify-center"><button onClick={()=>navigate("/commitments")} className="text-[11px]" style={{color:accent}}>Add commitment →</button></div>
      : items.map(c=>{
        const overdue = c.due_date&&c.due_date<now;
        return (
          <div key={c.commitment_id} className="flex items-start gap-2 px-2.5 py-2 rounded-xl" style={{background:"var(--cresc-surface-2)"}}>
            <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{background:overdue?"#EF4444":accent}}/>
            <div className="min-w-0">
              <p className="text-[11px] truncate text-white">{c.title}</p>
              {c.due_date&&<p className="text-[9px]" style={{color:overdue?"#EF4444":"var(--cresc-text-3)"}}>{new Date(c.due_date).toLocaleDateString([],{month:"short",day:"numeric"})}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CompassWidget({ wsId, accent }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(()=>{ if(wsId) api.get(`/workspaces/${wsId}/compass`).then(({data})=>setD(data)).catch(()=>{}); },[wsId]);
  return (
    <div className="flex flex-col h-full gap-3">
      {!d?.mission&&!d?.goals?.length ? (
        <div className="flex-1 flex items-center justify-center"><button onClick={()=>navigate("/compass")} className="text-[11px]" style={{color:accent}}>Set your Compass →</button></div>
      ) : <>
        {d.mission&&<p className="text-[11.5px] italic leading-relaxed" style={{color:"var(--cresc-text-2)"}}>&ldquo;{d.mission}&rdquo;</p>}
        {d.goals?.slice(0,3).map((g,i)=>(
          <div key={i} className="flex items-center gap-2">
            <div className="w-1 h-1 rounded-full" style={{background:accent}}/>
            <p className="text-[11px] truncate flex-1 text-white">{g.title}</p>
          </div>
        ))}
      </>}
    </div>
  );
}

function DriftWidget({ wsId, accent }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(()=>{ if(wsId) api.get(`/workspaces/${wsId}/drift`).then(({data:x})=>setD(x)).catch(()=>{}); },[wsId]);
  const score = d?.drift_score??null;
  const color = score===null?accent:score<30?"#00C781":score<60?"#F5C842":"#EF4444";
  const label = score===null?"—":score<30?"Aligned":score<60?"Drifting":"Off course";
  const r=22, circ=2*Math.PI*r;
  return (
    <div className="flex flex-col h-full gap-3 items-center justify-center">
      {score!==null&&(
        <div className="relative w-24 h-24">
          <svg viewBox="0 0 56 56" className="w-24 h-24 -rotate-90">
            <circle cx="28" cy="28" r={r} fill="none" stroke="var(--cresc-border)" strokeWidth="4"/>
            <circle cx="28" cy="28" r={r} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round"
              strokeDasharray={`${(1-score/100)*circ} ${circ}`} style={{transition:"stroke-dasharray 1s ease"}}/>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[20px] font-bold text-white">{score}</span>
          </div>
        </div>
      )}
      <div className="text-center">
        <p className="text-[14px] font-semibold" style={{color}}>{label}</p>
        {d?.total_activity_30d!==undefined&&<p className="text-[10px] mt-0.5" style={{color:"var(--cresc-text-3)"}}>{d.total_activity_30d} signals · 30d</p>}
      </div>
      <button onClick={()=>navigate("/drift")} className="text-[10px]" style={{color:accent}}>Full report →</button>
    </div>
  );
}

function TensionWidget({ wsId, accent }) {
  const [tensions, setTensions] = useState([]);
  useEffect(()=>{ if(wsId) api.get(`/workspaces/${wsId}/tension`).then(({data})=>setTensions(data?.tensions||[])).catch(()=>{}); },[wsId]);
  return (
    <div className="flex flex-col h-full gap-1.5 overflow-y-auto" style={{scrollbarWidth:"none"}}>
      {tensions.length===0 ? (
        <div className="flex-1 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" style={{color:"#00C781"}}/><p className="text-[11px]" style={{color:"#00C781"}}>No conflicts</p></div>
      ) : tensions.slice(0,4).map(t=>{
        const c = t.severity==="high"?"#EF4444":t.severity==="medium"?"#F5C842":"#5A90F5";
        return (
          <div key={t.tension_id} className="flex items-start gap-2 p-2.5 rounded-xl" style={{background:`${c}12`,border:`1px solid ${c}25`}}>
            <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" style={{color:c}}/>
            <p className="text-[10.5px] text-white">{t.title}</p>
          </div>
        );
      })}
    </div>
  );
}

function TimeDebtWidget({ wsId, accent }) {
  const [d, setD] = useState(null);
  useEffect(()=>{ if(wsId) api.get(`/workspaces/${wsId}/time-debt`).then(({data:x})=>setD(x)).catch(()=>{}); },[wsId]);
  const color = !d?accent:d.total_hours===0?"#00C781":d.total_hours<8?"#F5C842":"#EF4444";
  return (
    <div className="flex flex-col h-full gap-2">
      {!d ? <RefreshCw className="w-4 h-4 animate-spin m-auto" style={{color:accent}}/>
      : d.total_hours===0 ? <div className="flex-1 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" style={{color:"#00C781"}}/><p className="text-[11px]" style={{color:"#00C781"}}>No time debt</p></div>
      : <>
        <div className="flex items-baseline gap-1.5">
          <span className="text-[26px] font-bold text-white">{d.total_hours}h</span>
          <span className="text-[10px]" style={{color:"var(--cresc-text-3)"}}>≈ {d.total_days}d deferred</span>
        </div>
        {d.items.slice(0,4).map((item,i)=>(
          <div key={i} className="flex items-center gap-2">
            <div className="w-1 h-1 rounded-full" style={{background:color}}/>
            <p className="text-[10.5px] truncate flex-1 text-white">{item.title}</p>
            <span className="text-[9.5px] font-mono" style={{color:"var(--cresc-text-3)"}}>{item.hours}h</span>
          </div>
        ))}
      </>}
    </div>
  );
}

function BuoyWidget({ wsId, accent }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(()=>{ if(wsId) api.get(`/workspaces/${wsId}/buoy`,{params:{days:14}}).then(({data})=>setItems(data?.items||[])).catch(()=>{}); },[wsId]);
  const PATHS = { boards:"/boards",capture:"/capture",commitments:"/commitments",ab:"/ab" };
  return (
    <div className="flex flex-col h-full gap-1.5 overflow-y-auto" style={{scrollbarWidth:"none"}}>
      {items.length===0 ? <div className="flex-1 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" style={{color:"#00C781"}}/><p className="text-[11px]" style={{color:"#00C781"}}>Nothing neglected</p></div>
      : items.slice(0,5).map(item=>(
        <button key={item.id} onClick={()=>navigate(PATHS[item.tab]||"/")} className="flex items-center gap-2 text-left group">
          <div className="w-1.5 h-1.5 rounded-full" style={{background:accent}}/>
          <p className="text-[10.5px] truncate flex-1 text-white">{item.title}</p>
          <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100" style={{color:"var(--cresc-text-3)"}}/>
        </button>
      ))}
    </div>
  );
}

function getWidgetComponent(type) {
  const map = {
    ai: AIWidget, daily: TodayWidget, capture: CaptureWidget,
    boards: BoardsWidget, commitments: CommitmentsWidget,
    compass: CompassWidget, drift: DriftWidget, tension: TensionWidget,
    timedebt: TimeDebtWidget, buoy: BuoyWidget,
  };
  return map[type] || null;
}

// ─── Theme picker ─────────────────────────────────────────────────────────────
function ThemePicker({ onClose }) {
  const { themeId, setTheme } = useTheme();
  return (
    <div className="fixed inset-0 z-[500] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="rounded-2xl p-6 w-full max-w-sm shadow-2xl" onClick={e=>e.stopPropagation()}
        style={{background:"var(--cresc-surface)",border:"1px solid var(--cresc-border)"}}>
        <div className="flex items-center justify-between mb-5">
          <p className="text-[14px] font-semibold" style={{color:"var(--cresc-text)"}}>Theme</p>
          <button onClick={onClose}><X className="w-4 h-4" style={{color:"var(--cresc-text-3)"}}/></button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {Object.values(THEMES).map(t=>(
            <button key={t.id} onClick={()=>{setTheme(t.id);onClose();}}
              className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all"
              style={{borderColor:themeId===t.id?t.vars["--cresc-accent"]:"var(--cresc-border)",background:themeId===t.id?t.vars["--cresc-accent"]+"12":"transparent"}}>
              <div className="w-10 h-10 rounded-xl flex items-end p-1.5 border" style={{background:t.preview,borderColor:t.vars["--cresc-accent"]+"50"}}>
                <div className="w-full h-2 rounded" style={{background:t.vars["--cresc-accent"]}}/>
              </div>
              <span className="text-[10px] font-medium" style={{color:themeId===t.id?"var(--cresc-accent)":"var(--cresc-text-2)"}}>{t.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Widget picker ─────────────────────────────────────────────────────────────
function WidgetPicker({ layout, onAdd, onClose }) {
  const added = new Set(layout.map(w=>w.type));
  return (
    <div className="fixed inset-0 z-[500] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="rounded-2xl p-6 w-full max-w-sm shadow-2xl" onClick={e=>e.stopPropagation()}
        style={{background:"var(--cresc-surface)",border:"1px solid var(--cresc-border)"}}>
        <div className="flex items-center justify-between mb-4">
          <p className="text-[14px] font-semibold" style={{color:"var(--cresc-text)"}}>Add widget</p>
          <button onClick={onClose}><X className="w-4 h-4" style={{color:"var(--cresc-text-3)"}}/></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(WIDGET_DEFS).map(([type,def])=>{
            const Icon = def.icon;
            const isAdded = added.has(type);
            return (
              <button key={type} onClick={()=>!isAdded&&(onAdd(type),onClose())} disabled={isAdded}
                className="flex items-center gap-2.5 p-3 rounded-xl text-left transition-all"
                style={{border:"1px solid var(--cresc-border)",opacity:isAdded?.35:1,background:"var(--cresc-surface-3)"}}
                onMouseEnter={e=>{if(!isAdded)e.currentTarget.style.borderColor=def.accent+"60";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--cresc-border)";}}>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{background:def.accent+"25"}}>
                  <Icon className="w-3.5 h-3.5" style={{color:def.accent}}/>
                </div>
                <span className="text-[11px] font-medium" style={{color:"var(--cresc-text)"}}>{def.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Resizable widget card ────────────────────────────────────────────────────
function WidgetCard({ widget, containerWidth, onRemove, onResize, dragSrc, onDragStart, onDragOver, onDrop, isDragOver, children }) {
  const def = WIDGET_DEFS[widget.type];
  if (!def) return null;
  const Icon = def.icon;
  const [accent] = [def.accent];

  // Resize drag state
  const resizing = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const startW = useRef(widget.w);
  const startH = useRef(widget.h);
  const colW = containerWidth ? (containerWidth + GAP) / COLS : 0;

  const onResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = true;
    startX.current = e.clientX;
    startY.current = e.clientY;
    startW.current = widget.w;
    startH.current = widget.h;

    const onMove = (ev) => {
      if (!resizing.current) return;
      const dx = ev.clientX - startX.current;
      const dy = ev.clientY - startY.current;
      // Snap width: each column = colW px, snap threshold = colW/2
      const rawCols = startW.current + dx / colW;
      const newW = Math.max(1, Math.min(COLS, Math.round(rawCols)));
      // Snap height: each row = ROW_H px
      const rawRows = startH.current + dy / ROW_H;
      const newH = Math.max(1, Math.min(5, Math.round(rawRows)));
      onResize(widget.id, newW, newH);
    };
    const onUp = () => {
      resizing.current = false;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const cardH = widget.h * ROW_H + (widget.h - 1) * GAP;

  return (
    <div
      className="relative rounded-2xl flex flex-col overflow-hidden transition-all"
      style={{
        gridColumn: `span ${widget.w}`,
        height: cardH,
        background: "var(--cresc-surface)",
        border: `1px solid ${isDragOver ? accent+"60" : "var(--cresc-border)"}`, 
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      }}
      onDragOver={e=>{e.preventDefault();onDragOver(widget.id);}}
      onDrop={e=>{e.preventDefault();onDrop(widget.id);}}>
      
      {/* Colored top accent stripe */}
      <div className="h-[2px] w-full flex-shrink-0" style={{background:accent}}/>

      {/* Header */}
      <div className="flex items-center gap-2 px-4 pt-3 pb-2 flex-shrink-0">
        <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" style={{background:`${accent}25`}}>
          <Icon className="w-3.5 h-3.5" style={{color:accent}}/>
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-[0.1em] flex-1" style={{color:"var(--cresc-text-3)"}}>{def.label}</span>
        {/* Drag handle */}
        <div draggable onDragStart={()=>onDragStart(widget.id)}
          className="p-1 rounded cursor-grab active:cursor-grabbing opacity-30 hover:opacity-80 transition-opacity"
          style={{color:"var(--cresc-text-2)"}}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
            <circle cx="2" cy="2" r="1"/><circle cx="5" cy="2" r="1"/><circle cx="8" cy="2" r="1"/>
            <circle cx="2" cy="5" r="1"/><circle cx="5" cy="5" r="1"/><circle cx="8" cy="5" r="1"/>
            <circle cx="2" cy="8" r="1"/><circle cx="5" cy="8" r="1"/><circle cx="8" cy="8" r="1"/>
          </svg>
        </div>
        {/* Remove */}
        <button onClick={()=>onRemove(widget.id)}
          className="p-1 rounded opacity-20 hover:opacity-70 transition-opacity"
          style={{color:"var(--cresc-text-2)"}}>
          <X className="w-3 h-3"/>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 pb-4 min-h-0 overflow-hidden">
        {children}
      </div>

      {/* Resize handle — bottom-right corner */}
      <div
        onPointerDown={onResizeStart}
        className="absolute bottom-1 right-1 w-5 h-5 flex items-end justify-end cursor-se-resize opacity-0 hover:opacity-60 transition-opacity"
        style={{color:accent}}>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" opacity="0.8">
          <path d="M10 0 L10 10 L0 10 Z" />
        </svg>
      </div>
    </div>
  );
}

// ─── Main WorkspaceDashboard ──────────────────────────────────────────────────
export default function WorkspaceDashboard() {
  const { current } = useWorkspace();
  const { user } = useAuth();
  const wsId = current?.workspace_id;
  const [layout, setLayout] = useState(()=>loadLayout()||DEFAULT_LAYOUT);
  const [showPicker, setShowPicker] = useState(false);
  const [showTheme, setShowTheme] = useState(false);
  const dragSrc = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  const [time, setTime] = useState(new Date());
  const gridRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(()=>{
    const t = setInterval(()=>setTime(new Date()),30000);
    return ()=>clearInterval(t);
  },[]);

  useEffect(()=>{
    const h=()=>setShowTheme(true);
    window.addEventListener("crescendo:open-theme",h);
    return ()=>window.removeEventListener("crescendo:open-theme",h);
  },[]);

  // Measure grid container width for resize snapping
  useEffect(()=>{
    if (!gridRef.current) return;
    const ro = new ResizeObserver(entries=>{
      for (const e of entries) setContainerWidth(e.contentRect.width);
    });
    ro.observe(gridRef.current);
    setContainerWidth(gridRef.current.getBoundingClientRect().width);
    return ()=>ro.disconnect();
  },[]);

  const firstName = user?.name?.split(" ")[0]||"";
  const update = (next)=>{ setLayout(next); saveLayout(next); };
  const addWidget = (type)=>{ const d=WIDGET_DEFS[type]; update([...layout,{id:`w${Date.now()}`,type,w:d.defaultW,h:d.defaultH}]); };
  const removeWidget = (id)=>update(layout.filter(w=>w.id!==id));
  const resizeWidget = (id,w,h)=>update(layout.map(x=>x.id===id?{...x,w,h}:x));
  const onDragStart=(id)=>{ dragSrc.current=id; };
  const onDragOver=(id)=>{ if(id!==dragSrc.current) setDragOver(id); };
  const onDrop=(targetId)=>{
    if(!dragSrc.current||dragSrc.current===targetId){ setDragOver(null); return; }
    const next=[...layout];
    const si=next.findIndex(w=>w.id===dragSrc.current);
    const ti=next.findIndex(w=>w.id===targetId);
    if(si>=0&&ti>=0)[next[si],next[ti]]=[next[ti],next[si]];
    update(next); dragSrc.current=null; setDragOver(null);
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto" style={{background:"var(--cresc-bg)",scrollbarWidth:"none"}}>
      {/* Header */}
      <div className="flex-shrink-0 px-8 pt-7 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{background:"var(--cresc-accent)25"}}>
                <Sparkles className="w-3.5 h-3.5" style={{color:"var(--cresc-accent)"}}/>
              </div>
              <h1 className="text-[22px] font-bold tracking-tight" style={{color:"var(--cresc-text)"}}>
                {greet()}{firstName?`, ${firstName}`:""}
              </h1>
            </div>
            <p className="text-[12px] pl-8" style={{color:"var(--cresc-text-3)"}}>
              {time.toLocaleDateString([],{weekday:"long",month:"long",day:"numeric"})}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={()=>setShowTheme(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] transition-all"
              style={{border:"1px solid var(--cresc-border)",color:"var(--cresc-text-3)"}}>
              <Palette className="w-3.5 h-3.5"/> Theme
            </button>
            <button onClick={()=>setShowPicker(true)}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-[12px] font-semibold transition-all"
              style={{background:"var(--cresc-accent)",color:"#000"}}>
              <Plus className="w-3.5 h-3.5"/> Widget
            </button>
            <button onClick={()=>update(DEFAULT_LAYOUT)}
              className="px-3 py-1.5 rounded-xl text-[11px] transition-all"
              style={{border:"1px solid var(--cresc-border)",color:"var(--cresc-text-3)"}}>
              Reset
            </button>
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 mx-8 h-px mb-5" style={{background:"var(--cresc-border-sub)"}}/>

      {/* Widget grid */}
      <div ref={gridRef} className="flex-1 px-6 pb-8"
        style={{display:"grid",gridTemplateColumns:`repeat(${COLS},1fr)`,gap:GAP,alignContent:"start"}}>
        {layout.map(widget=>{
          const Component = getWidgetComponent(widget.type);
          const def = WIDGET_DEFS[widget.type];
          if (!Component||!def) return null;
          return (
            <WidgetCard key={widget.id} widget={widget} containerWidth={containerWidth}
              onRemove={removeWidget} onResize={resizeWidget}
              dragSrc={dragSrc} onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop}
              isDragOver={dragOver===widget.id}>
              <Component wsId={wsId} accent={def.accent}/>
            </WidgetCard>
          );
        })}
        {layout.length===0&&(
          <div className="col-span-4 flex flex-col items-center justify-center py-24 gap-4">
            <Sparkles className="w-8 h-8" style={{color:"var(--cresc-text-4)"}}/>
            <p className="text-[14px] font-semibold" style={{color:"var(--cresc-text-3)"}}>No widgets</p>
            <button onClick={()=>setShowPicker(true)} className="px-5 py-2.5 rounded-xl text-[12px] font-semibold"
              style={{background:"var(--cresc-accent)",color:"#000"}}>
              Add your first widget
            </button>
          </div>
        )}
      </div>

      {showPicker&&<WidgetPicker layout={layout} onAdd={addWidget} onClose={()=>setShowPicker(false)}/>}
      {showTheme&&<ThemePicker onClose={()=>setShowTheme(false)}/>}
    </div>
  );
}
